/* eslint-disable @typescript-eslint/no-misused-promises */
import { yupResolver } from '@hookform/resolvers/yup';
import { Autocomplete, Box, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import character from '../../assets/images/intake/step2/char.svg';
import { useUserAsaTaskHistoryContext } from '../../hooks/use-user-asa-task-history-context.hooks';
import { useAuthContext } from '../../hooks/use-user-auth-context.hooks';
import {
  AsaTaskName,
  AsaUserTasks,
  completeASATask,
  dispatchUpdateAsaTaskHistoryInfo,
} from '../../redux/slice/asa-task-history';
import { CustomUser } from '../../redux/slice/auth/CustomUser';
import {
  CareerRoadMapDto,
  DetailOrientationDto,
} from '../../redux/slice/career-roadmap/career-roadmap-type';
import { dispatchCareerRoadMapAssessment } from '../../redux/slice/career-roadmap/career-roadmap.operations';
import { roadMapQuestions } from '../../redux/slice/career-roadmap/careerRoadMapQuestions';
import { RootState, useAppDispatch } from '../../redux/store';
import logger from '../../services/logger';
import { EnvironmentVariable } from '../../utils/constants';
import { buildASANextTaskUrl } from '../../utils/helper';
import { CircularLoader } from '../loader/CircularLoader';

interface ComponentProps {
  step: number;
  onBack: () => void;
}

interface FormData extends DetailOrientationDto {}

const CareerRoadMapSchema = yup
  .object({
    details: yup.string().required('Please select a option'),
  })
  .required();

export const Availability: React.FC<ComponentProps> = ({ step, onBack }) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const availabilityState = useSelector((state: RootState) => state.careerRoadmap.availability);
  const currentUser: CustomUser | null = useAuthContext();
  const [currsentAsaTask, setCurrentAsaTask] = useState<AsaUserTasks | undefined>(undefined);
  const asaUserTaskHistory = useUserAsaTaskHistoryContext();

  useEffect(() => {
    if (
      currentUser?.claims?.isAsaUser &&
      Object.keys(asaUserTaskHistory).length &&
      asaUserTaskHistory?.tasks?.length
    ) {
      const currentTask = asaUserTaskHistory.tasks?.find(
        (x) => !x.isCompleted && x.taskName === AsaTaskName.CAREERROADMAP,
      );

      setCurrentAsaTask(currentTask);

      if (currentTask && !currentTask?.startDate) {
        void dispatch(
          dispatchUpdateAsaTaskHistoryInfo({
            asaUserId: currentUser?.claims?.asaUserId || 0,
            taskId: currentTask?.taskId || '',
            taskStep: currentTask?.taskStep || 1,
            startDate: currentTask?.startDate || new Date(),
            taskName: AsaTaskName.CAREERROADMAP,
          }),
        );
      }

      if (currsentAsaTask && (currsentAsaTask?.taskStep as number) != 13) {
        if (currsentAsaTask?.taskUrl) {
          const url = new URL(currsentAsaTask?.taskUrl || '');
          navigate(`${url.pathname}${url.search}`);
          // setIsDisabled(true);
        }
      }
    }
  }, [asaUserTaskHistory, currentUser, currsentAsaTask]);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(CareerRoadMapSchema),
    defaultValues: {
      details: availabilityState?.answer || '',
    },
  });

  const backButtonClick = () => {
    onBack();
  };

  const submitCareerRoadMapAnswer: SubmitHandler<FormData> = async (data: FormData) => {
    logger.debug('Submitted personal info.', data);
    setIsLoading(true);

    try {
      if (data) {
        const question5: CareerRoadMapDto = {
          id: roadMapQuestions[15].questionNumber,
          answer: data?.details,
          question: roadMapQuestions[15].question,
        };
        if (!availabilityState?.answer || availabilityState.answer !== data.details) {
          question5 && (await dispatch(dispatchCareerRoadMapAssessment(question5, 'availability')));
        }
        if (currentUser?.claims?.isAsaUser && currsentAsaTask) {
          const endPointUrl = process.env[EnvironmentVariable.REACT_APP_ASA_COMPLETE_TASK_URL];
          const isStaging =
            process.env[EnvironmentVariable.REACT_APP_BASE_URL]?.includes('staging') ||
            process.env[EnvironmentVariable.REACT_APP_BASE_URL]?.includes('localhost');
          const taskResponse = await completeASATask(
            {
              asaUserId: currentUser?.claims?.asaUserId as number,
              taskId: currsentAsaTask?.taskId,
              taskStep: currsentAsaTask?.taskStep || step,
            },
            endPointUrl || '',
            isStaging || false,
          );

          if (!taskResponse || !taskResponse?.ok) {
            toast.error('Something went wrong!, Please try after sometime.');
            return;
          }

          let url = new URL(currsentAsaTask?.taskUrl || '');
          const nextTaskUrl = buildASANextTaskUrl(url, AsaTaskName.CAREERROADMAP, step + 1);
          url = new URL(nextTaskUrl || '');
          await dispatch(
            dispatchUpdateAsaTaskHistoryInfo({
              startDate: new Date(),
              asaUserId: currentUser?.claims?.asaUserId || 0,
              taskId: currsentAsaTask?.taskId || '',
              taskStep:
                (currsentAsaTask && parseInt(currsentAsaTask?.taskStep.toString()) + 1) || 13,
              taskUrl: nextTaskUrl,
              taskName: AsaTaskName.CAREERROADMAP,
              totalSteps: 17,
            }),
          );

          await dispatch(
            dispatchUpdateAsaTaskHistoryInfo({
              asaUserId: currentUser?.claims?.asaUserId || 0,
              taskId: currsentAsaTask?.taskId || '',
              taskStep: currsentAsaTask?.taskStep || step,
              isCompleted: true,
              completedDate: new Date(),
            }),
          );
          toast.success('Task has been successfully submitted');
          navigate(`${url.pathname}${url.search}`);
        } else {
          navigate(`/career-roadmap/step${step + 1}`);
        }
      }
    } catch (error) {
      setIsLoading(false);
    }
    setIsLoading(false);
  };
  useEffect(() => {
    reset({
      details: availabilityState?.answer,
    });
  }, [reset, availabilityState?.answer]);

  useEffect(() => {
    window.scrollTo(0, 0);
  });

  return (
    <div>
      {isLoading && <CircularLoader />}

      <div className="flex mt-9 mb-10 md:px-10">
        <div className="bg-gray-200 p-4 rounded-lg font-semibold mr-5 flex items-center">
          {roadMapQuestions[15].questionNumber}
        </div>
        <div>
          <h4 className="text-2xl">
            <span className="font-semibold">{roadMapQuestions[15].question} </span>
          </h4>
        </div>
      </div>
      <div className="character absolute bottom-0 right-[-85px] lg:right-[-138px]">
        <img className="" src={character} alt="" />
      </div>
      <div className="flex mt-9 mb-10 md:px-10">
        <Box
          className="w-full"
          component="form"
          sx={{ '& > :not(style)': { mb: 4 } }}
          autoComplete="off"
          onSubmit={handleSubmit(submitCareerRoadMapAnswer)}
        >
          <Controller
            control={control}
            name="details"
            render={({ field: { value, onChange } }) => (
              <Autocomplete
                freeSolo
                options={roadMapQuestions[15]?.options || []}
                value={value || ''}
                onChange={(_, newValue) => onChange(newValue)}
                onInputChange={(_, newInputValue) => onChange(newInputValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Options"
                    placeholder="Type or select an option..."
                    required
                    error={!!errors.details}
                    helperText={
                      errors.details?.message ||
                      'You may select from the list or type your own answer'
                    }
                  />
                )}
              />
            )}
          />

          <div className="flex justify-end lg:justify-between lg:mt-20 mt-10">
            <button onClick={handleSubmit(backButtonClick)} className="btn-internal" type="button">
              Back
            </button>
            <button className="btn-internal ml-5" disabled={isLoading} type="submit">
              Next
            </button>
          </div>
        </Box>
      </div>
    </div>
  );
};
